import { appRoute } from "@/components/routes/app-route";
import { AppPlace } from "@/components/routes/app-route.zod";
import { Itinerary, ItineraryIcon, ItineraryIconProps, ItineraryItemProps } from "@/components/ui/containers/timeline";
import Typography from "@/components/ui/typeography/typeography";
import { ArmpLocationPin } from "@/components/ui/icons.custom";
import { MAP_ASSETS } from "@/config/assets";
import { sentenceCaseFormatter } from "@/lib/formatters/string-formatters";
import { AppItineraryOrNode, AppLeg } from "@/types/otp/+extended";
import { ItineraryDetailsLeg } from "./itinerary-details.leg";
import { getItineraryTimeLabel } from "@/lib/formatters/availability-formatters";

export function ItineraryDetailsLegs({ startTime, endTime, legs }: AppItineraryOrNode) {
    const from = appRoute.useSearch({ select: (s) => s.from })
    const to = appRoute.useSearch({ select: (s) => s.to })

    if (!startTime || !endTime || !legs || !from || !to) {
        return;
    }
    const transformedLegs = transformLegsToStops(from, to, startTime, endTime, legs)

    if (!transformedLegs || transformedLegs.length <= 0) {
        return;
    }
    const timeLabel = getItineraryTimeLabel({ startTime, endTime, legs });

    return (
        <span className="flex flex-col gap-4">
            <div className="flex flex-col">
                <Typography variant={"h3"} color={"foreground"}>Itinerary</Typography>

                {timeLabel && (
                    <span
                        className="text-sm flex flex-row justify-start items-center gap-1.5"
                    >
                        <p className="text-muted-foreground">{timeLabel.label}:</p>

                        <p className="">{timeLabel.value}</p>
                    </span>
                )}
            </div>

            <Itinerary className="mb-4">
                {/** Legs */}
                {transformedLegs.map((l) => {
                    return (
                        <ItineraryDetailsLeg
                            key={`${l.stop.time.toUTCString()}-${l.stop.title}`}
                            {...l}
                        />
                    )
                })}
            </Itinerary>
        </span>
    )
}

export interface StopWithLeg {
    stop: {
        time: Date,
        title: string,
        description?: string,
        icon?: React.ReactElement<ItineraryIconProps>;
        props?: Partial<ItineraryItemProps>
    },
    leg?: AppLeg,
}

function transformLegsToStops(from: AppPlace, to: AppPlace, startTime: number, endTime: number, legs: AppItineraryOrNode["legs"]) {
    if (!legs) {
        return [];
    }

    const stopWithLegList: StopWithLeg[] = []

    for (let i = 0; i < legs.length; i++) {
        const leg = legs[i]
        if (!leg) {
            break;
        }

        if (i === 0) {
            stopWithLegList.push({
                    stop: {
                        time: new Date(startTime),
                        title: from.key,
                        icon: <ItineraryIcon variant={"secondary"} icon={<img src={MAP_ASSETS.marker.start.iconUrl} className="aspect-square w-4 h-4" alt="Origin marker" />} />,
                    props: {
                        title: from.key,
                    }
                },
                leg: leg
            })
            continue;
        }

        if (leg.startTime) {
            stopWithLegList.push({
                stop: {
                    time: new Date(leg.startTime),
                    title: sentenceCaseFormatter(tryGetLegTitle(leg))
                },
                leg: leg
            })
            continue;
        }
    }

    stopWithLegList.push({
            stop: {
            time: new Date(endTime),
            title: to.key,
            icon: <ItineraryIcon variant={"secondary"} icon={<ArmpLocationPin className="text-[#29275B] size-4" />} />,
            props: {
                title: to.key,
                borderStyling: "none"
            }
        },
    })

    return stopWithLegList;
}

function tryGetLegTitle(leg: AppLeg) {
    return  leg.from?.name 
        ?? leg.route?.longName
        ?? leg.agency?.name
        ?? leg.mode
        ?? "N/A"
}
